import { ExpandLess } from "@mui/icons-material";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import {
  Button,
  ClickAwayListener,
  Collapse,
  MenuItem,
  Paper,
  Select,
} from "@mui/material";
import { DatePickerWorkDay } from "components/CustomDatePicker";
import CustomDialog from "components/CustomDialog";
import CustomLink from "components/CustomLink";
import CustomSelectStatusBadge from "components/CustomSelectStatusBadge";
import { CustomStatusBadge } from "components/StatusBadge";
import EditFormControl from "components/form/EditFormControl";
import CustomText from "components/typography/CustomText";
import {
  getCurrentWeekNum,
  getWeekDateByWeekNumber,
  getWeekDaysByWeekNumber,
} from "lib/date";
import { currency } from "lib/formatter";
import {
  orderStatusOptions,
  paymentStatusOption,
  postcodes,
  refundOptions,
} from "lib/global";
import { serverGet, serverPost } from "lib/serverRequest";
import { isEmpty } from "lodash";
import { getBoxDate, getNewBoxDates } from "models/boxesModel";
import { addressDetailInfo } from "models/third/addressRight";
import { getLabel } from "models/third/freightways";
import { checkWeeklySlotLog } from "models/weeklySlotsModel";
import moment from "moment";
import { useSnackbar } from "notistack";
import { useEffect, useState } from "react";
import "react-datepicker/dist/react-datepicker.css";
import { useSelector } from "react-redux";
import { openLoading } from "store/reducers/tools";
import BoxEditForm from "./BoxEditForm";

export default function OrderSummaryInfo({
  data = {},
  onUpdate = () => {},
  slotMove = () => {},
}) {
  const [open, setOpen] = useState(false);
  const [boxInfo, setBoxInfo] = useState({});
  const [slotInfo, setSlotInfo] = useState({});
  const [refundType, setRefundType] = useState("none");
  const [refundReason, setRefundReason] = useState("");
  const [refundOpen, setRefundOpen] = useState(false);
  const [movingOpen, setMovingOpen] = useState(false);
  const { enqueueSnackbar } = useSnackbar();
  const [disableDay, setDisableDay] = useState("");
  const [blackoutDates, setBlackoutDates] = useState([]);
  const [fileUrl, setFileUrl] = useState("");
  const prevSlotInfo = useSelector((state) => state?.app?.orderInfo ?? {});
  const authUser = useSelector((state) => state?.auth?.user ?? {});

  const handleUpdate = () => {
    const log = checkWeeklySlotLog({
      newValue: slotInfo,
      initData: prevSlotInfo,
    });
    serverPost({
      url: "weeklySlots/update",
      data: {
        ...slotInfo,
        slotId: slotInfo?.id,
        log_data: Boolean(log)
          ? {
              user_id: data?.user_id ?? "",
              changer_id: authUser?.id,
              changer_type: "admin",
              box_id: data?.box_id,
              weekly_slot_id: data?.id ?? "",
              log: `${authUser?.name} ( admin ) - Order #${slotInfo?.id} - Update ${log}`,
            }
          : {},
      },
      onFinish: (res, err) => {
        onUpdate(res, err);
        setOpen(false);
      },
    });
  };

  const loadData = async () => {
    const {
      dispatch_date = "",
      delivery_date = "",
      baked_date = "",
    } = await getBoxDate(data);
    //set disable friday delivery
    const addressDetails = await addressDetailInfo(data?.address?.id);
    if (
      !postcodes.some(
        (postcode) => postcode === addressDetails?.structured_address?.postcode
      )
    ) {
      setDisableDay(1);
    }
    setSlotInfo({
      ...data,
      dispatch_date: dispatch_date,
      delivery_date: delivery_date,
      baked_date: baked_date,
    });
    setBoxInfo({
      ...data,
      dispatch_date: dispatch_date,
      delivery_date: delivery_date,
      baked_date: baked_date,
    });
  };

  const calcMindate = () => {
    let date = new Date();
    if (Boolean(data?.payment_intent?.id)) {
      return date.setDate(date.getDate() + 2);
    } else {
      const currentWeek = getCurrentWeekNum();
      return getWeekDateByWeekNumber(currentWeek + 2, 0);
    }
  };

  const loadBlackoutDates = () => {
    serverGet({
      url: "blackout-day",
      onFinish: (res, err) => {
        if (res === false) {
          return;
        }
        setBlackoutDates((s = []) =>
          res.map((item) => new Date(item.blackout_day))
        );
      },
    });
  };

  const handleChangeDate = async (date = new Date(), type = "dispatch") => {
    const newWeekInfo = await getNewBoxDates(date, type, slotInfo);
    let newBox = { ...slotInfo, ...newWeekInfo };
    const {
      dispatch_date = "",
      delivery_date = "",
      baked_date = "",
    } = await getBoxDate(newBox);
    setSlotInfo((prev) => ({
      ...prev,
      ...newWeekInfo,
      dispatch_date: dispatch_date,
      delivery_date: delivery_date,
      baked_date: baked_date,
    }));
  };

  const handleChangeStatus = (e) => {
    if (e?.name === "address") {
      setSlotInfo((s = {}) => ({
        ...s,
        ...{ [e?.name]: e?.value ?? {} },
      }));
      return;
    }
    e.stopPropagation();
    const { name, value } = e.target;
    let updateData = { [name]: value };
    if (value === "4") {
      updateData = {
        status: "4",
        paid: "4",
      };
    } else {
      if (name === "paid" && slotInfo.status === "4") {
        updateData = {
          [name]: value,
          status: "0",
        };
      } else if (name === "status" && slotInfo.paid === "4") {
        updateData = {
          [name]: value,
          paid: "0",
        };
      }
    }
    if (name === "paid" && value === "5") {
      updateData = {
        [name]: value,
        status: "2",
      };
    }
    if (name === "status" && value === "2")
      updateData = { [name]: value, paid: "5" };
    setSlotInfo((s = {}) => ({
      ...s,
      ...updateData,
    }));
  };

  const handleCreateTicket = async () => {
    try {
      openLoading(true);
      const addressId = slotInfo?.address?.id ?? "";
      // const freightwaysAuth = await authFreightways();
      const addressInfo = await addressDetailInfo(addressId);
      serverPost({
        url: "freightways/consignment",
        data: {
          weekly_slot_id: slotInfo?.id,
          user_id: slotInfo?.user_id,
          address_info: addressInfo,
          // token: freightwaysAuth?.data?.access_token,
        },
        onFinish: async (res, err) => {
          if (res === false) {
            console.log(err, "err");
            enqueueSnackbar(
              err?.error?.status === 409
                ? "The consignment with consignmentId already exists"
                : "Tracking info is invalid.",
              {
                variant: "warning",
              }
            );
            openLoading(false);
            return;
          }
          const fileURL = await getLabel({
            tracking_info: res?.slot?.tracking_info ?? {},
            postCode: addressInfo?.structured_address?.postcode ?? "",
          });
          console.log(fileURL);
          setFileUrl(fileURL);
          setSlotInfo((s = {}) => ({
            ...s,
            tracking_info: res?.slot?.tracking_info ?? null,
          }));
          enqueueSnackbar("Successfully created.", {
            variant: "success",
          });
        },
      });
    } catch (err) {
      openLoading(false);
      enqueueSnackbar("Tracking info is invalid.", {
        variant: "warning",
      });
      console.log(err, "err");
    }
  };

  const handleRefund = () => {
    if (refundType === "none") {
      enqueueSnackbar("Select a reason type!", {
        variant: "warning",
      });
      return;
    }
    if (refundType === "4" && refundReason === "") {
      enqueueSnackbar("Please write the note!", {
        variant: "warning",
      });
      return;
    }
    openLoading(true);
    serverPost({
      url: "box/payment/refund",
      data: {
        boxInfo: slotInfo,
        refundType: refundType,
        refundReason: refundReason,
        authUser: authUser,
      },
      onFinish: (res, err) => {
        setRefundOpen(false);
        openLoading(false);
        if (res === false) {
          enqueueSnackbar(err.message, {
            variant: "warning",
          });
          return;
        }
        enqueueSnackbar("Payment Refunded successfully!", {
          variant: "success",
        });
        setSlotInfo((prev) => ({
          ...prev,
          status: res.status,
          paid: res.paid,
        }));
        setBoxInfo((prev) => ({ ...prev, status: res.status, paid: res.paid }));
      },
    });
  };

  const handleChangeDispatchDate = (date) => {
    if (boxInfo.paid === "2") setMovingOpen(true);
    handleChangeDate(date, "dispatch");
  };

  const handleMoveConfirm = () => {
    const newWeekCount = moment(slotInfo?.dispatch_date).isoWeek();
    const newDayCount = moment(slotInfo?.dispatch_date).isoWeekday();
    const newYearCount = moment(slotInfo?.dispatch_date).isoWeekYear();
    openLoading(true);
    serverPost({
      url: "weeklySlots/move_future_week",
      data: {
        weekly_slot_id: boxInfo.id,
        newWeekCount,
        newDayCount,
        newYearCount,
      },
      onFinish: (res, err) => {
        openLoading(false);
        if (res.message !== true)
          return enqueueSnackbar(err.message, { variant: "warning" });
        enqueueSnackbar("Order moved successfully!", { variant: "success" });
        slotMove(res.id);
        setOpen(true);
        setMovingOpen(false);
      },
    });
  };

  useEffect(() => {
    if (!isEmpty(data)) {
      loadData(data);
    }
    loadBlackoutDates();
    //eslint-disable-next-line
  }, [data]);

  useEffect(() => {
    if (Boolean(fileUrl)) {
      setTimeout(() => {
        window.frames["printffull"].focus();
        window.frames["printffull"].print();
        openLoading(false);
      }, 1000);
    }
    // eslint-disable-next-line
  }, [fileUrl]);
  return (
    <Paper sx={{ backdropFilter: "2px" }} className="relative">
      <div className="flex justify-between w-full hover:cursor-pointer border-b border-white-gray px-4">
        <div className="flex flex-1 py-3 items-center">
          <div className="py-1">
            <CustomText font="mont" variant="body1">
              Order Status
            </CustomText>
            <CustomText variant="body2" bold={600}>
              <CustomStatusBadge
                size="body2"
                text={
                  orderStatusOptions.find(
                    (item) =>
                      item.id ===
                      (boxInfo?.tracking_info ? "1" : boxInfo?.status ?? "0")
                  )?.label
                }
                variant={
                  orderStatusOptions.find(
                    (item) => item.id === (boxInfo?.status ?? "0")
                  )?.variant
                }
                bold
              />
            </CustomText>
          </div>
          <div className="flex flex-wrap flex-1 justify-evenly">
            <div className="px-2 md:px-4 py-1">
              <CustomText font="mont" variant="body1">
                CUT-OFF DATE
              </CustomText>
              <CustomText variant="body2" bold={600}>
                {getWeekDaysByWeekNumber({
                  yearNumber: boxInfo?.week_year,
                  weekNumber: (boxInfo?.week_next_num ?? boxInfo?.week_num) - 2,
                  addDay: 6,
                  format: "ddd DD MMM, YY",
                })}
              </CustomText>
            </div>
            <div className="px-2 md:px-4 py-1">
              <CustomText font="mont" variant="body1">
                BAKE DATE
              </CustomText>
              <CustomText variant="body2" bold={600}>
                {moment(boxInfo?.baked_date).format("DD MMM YYYY")}
              </CustomText>
            </div>
            <div className="px-2 md:px-4 py-1">
              <CustomText variant="body1">DISPATCH DATE</CustomText>
              <CustomText variant="body2" bold={600}>
                {moment(boxInfo?.dispatch_date).format("DD MMM YYYY") ?? ""}
              </CustomText>
            </div>
            <div className="px-2 md:px-4 py-1">
              <CustomText variant="body1">DELIVERY DATE</CustomText>
              <CustomText variant="body2" bold={600}>
                {moment(boxInfo?.delivery_date).format("DD MMM YYYY") ?? ""}
              </CustomText>
            </div>
            <div className="px-2 md:px-4 py-1">
              <CustomText variant="body1">PAID</CustomText>
              <CustomText variant="body2" bold={600}>
                <CustomStatusBadge
                  size="body2"
                  text={
                    paymentStatusOption.find(
                      (item) => item.id === (boxInfo?.paid ?? "0")
                    )?.label
                  }
                  variant={
                    paymentStatusOption.find(
                      (item) => item.id === (boxInfo?.paid ?? "0")
                    )?.variant
                  }
                />
              </CustomText>
            </div>
            {(Boolean(boxInfo?.payment_intent?.id) ||
              Boolean(boxInfo?.credit)) &&
              boxInfo?.paid === "1" && (
                <div className="px-2 md:px-4 py-1">
                  <CustomText variant="body1">PAYMENT</CustomText>
                  <CustomText variant="body2" bold={600}>
                    {Boolean(boxInfo?.credit)
                      ? "Account Credit"
                      : boxInfo?.payment_intent?.id}
                  </CustomText>
                </div>
              )}
          </div>
          <div className="pr-3">
            {slotInfo?.tracking_info?.consignmentId ? (
              <CustomText className="underline" variant="body2" bold>
                {slotInfo?.tracking_info?.consignmentId}
              </CustomText>
            ) : (
              <Button
                variant="contained"
                color="secondary"
                onClick={handleCreateTicket}
              >
                PRINT TICKET
              </Button>
            )}
          </div>
        </div>
        <div
          className="border-l border-white-gray flex items-center p-3"
          onClick={() => setOpen((prev) => !prev)}
        >
          <CustomText variant="body2">Edit</CustomText>
          {open ? <ExpandLess /> : <ExpandMoreIcon />}
        </div>
      </div>
      <Collapse in={open} unmountOnExit className="box-detail">
        <ClickAwayListener
          onClickAway={() => {
            // setOpen(false);
          }}
        >
          <Paper className="absolute z-10 top-0 left-0 w-full" elevation={4}>
            <div className="flex justify-between w-full hover:cursor-pointer border-b border-white-gray px-4">
              <div className="flex flex-1 py-3">
                <div className="w-40 md:w-56 py-1">
                  <CustomText font="mont" variant="body1">
                    Order Status
                  </CustomText>
                  <CustomSelectStatusBadge
                    value={slotInfo?.status || "0"}
                    options={orderStatusOptions}
                    name="status"
                    onChange={handleChangeStatus}
                  />
                </div>
                <div className="flex flex-wrap flex-1 justify-evenly">
                  <div className="px-2 md:px-4 py-1">
                    <CustomText font="mont" variant="body1">
                      BAKE DATE
                    </CustomText>
                    <DatePickerWorkDay
                      excludeDates={blackoutDates}
                      selected={slotInfo?.baked_date}
                      disabled={true}
                      onChange={(date) => handleChangeDate(date, "baked")}
                    />
                  </div>
                  <div className="px-2 md:px-4 py-1">
                    <CustomText variant="body1">DISPATCH DATE</CustomText>
                    <DatePickerWorkDay
                      excludeDates={blackoutDates}
                      selected={slotInfo?.dispatch_date}
                      minDate={calcMindate()}
                      friday={postcodes.includes(
                        slotInfo?.address?.postcode ?? ""
                      )}
                      disabled={Boolean(slotInfo?.tracking_info?.consignmentId)}
                      onChange={handleChangeDispatchDate}
                    />
                  </div>
                  <div className="px-2 md:px-4 py-1">
                    <CustomText variant="body1">DELIVERY DATE</CustomText>
                    <DatePickerWorkDay
                      disabled={true}
                      excludeDates={blackoutDates}
                      disabledays={disableDay}
                      selected={slotInfo?.delivery_date}
                      onChange={(date) => handleChangeDate(date, "delivery")}
                    />
                  </div>
                  <div className="px-2 md:px-4 py-1">
                    <CustomText variant="body1">PAID</CustomText>
                    <CustomSelectStatusBadge
                      name="paid"
                      value={slotInfo?.paid ?? "1"}
                      options={paymentStatusOption}
                      onChange={handleChangeStatus}
                    />
                  </div>
                  {Boolean(slotInfo?.payment_intent?.id) &&
                    slotInfo?.paid === "1" && (
                      <div className="px-2 md:px-4 py-1">
                        <CustomText variant="body1">PAYMENT</CustomText>
                        <Button
                          variant="outlined"
                          color="secondary"
                          onClick={() => setRefundOpen(true)}
                        >
                          Refund
                        </Button>
                      </div>
                    )}
                </div>
              </div>
              <div
                className="border-l border-white-gray flex items-center p-3"
                onClick={() => setOpen((prev) => !prev)}
              >
                <CustomText variant="body2">Edit</CustomText>
                {open ? <ExpandLess /> : <ExpandMoreIcon />}
              </div>
            </div>
            <div
              className={`border-b border-t px-4 ${
                boxInfo?.status?.value === "0"
                  ? "border-white"
                  : "border-white-gray "
              }`}
            >
              <CustomText className="py-4" variant="h6" bold={600}>
                Details
              </CustomText>
            </div>
            <div className="p-4">
              <BoxEditForm data={slotInfo} onChange={handleChangeStatus} />
              <div className="z-20">
                <div className="flex flex-start py-4">
                  <Button
                    color="secondary"
                    variant="contained"
                    onClick={handleUpdate}
                  >
                    Save Changes
                  </Button>
                  <Button
                    className="bg-light-gray ml-2"
                    variant="contained"
                    onClick={() => {
                      setOpen(false);
                      setSlotInfo(boxInfo);
                    }}
                  >
                    Cancel
                  </Button>
                </div>
              </div>
            </div>
          </Paper>
        </ClickAwayListener>
      </Collapse>
      <CustomDialog
        title="Refund Payment"
        maxWidth="md"
        open={refundOpen}
        onClose={() => setRefundReason(false)}
        onSave={handleRefund}
      >
        <>
          <div className="">
            <CustomText className="text-13" color="secondary">
              Refunds take 5-10 days to appear on a customer's statement.
              Stripe's fees for the original payment won't be returned, but
              there are no additional fees for the refund.
            </CustomText>
            <CustomLink
              className="text-primary font-bold text-13"
              to="https://support.stripe.com/questions/understanding-fees-for-refunded-payments"
              target="_blank"
            >
              <CustomText className="text-primary" bold>
                Learn more
              </CustomText>
            </CustomLink>
          </div>
          <div className="px-5 py-5">
            <div className="flex items-center py-1.5">
              <CustomText className="text-13 min-w-64">Refund</CustomText>
              <CustomText className="text-primary">
                {currency.format(data?.amount ?? "")}
              </CustomText>
            </div>
            <div className="flex items-center py-1.5">
              <CustomText className="text-13 min-w-64">Reason</CustomText>
              <Select
                size="small"
                sx={{ background: "#fff", width: 256 }}
                value={refundType}
                onChange={(e) => setRefundType(e.target.value)}
              >
                <MenuItem value="none">Select a reason</MenuItem>
                {refundOptions?.map((item) => (
                  <MenuItem value={item?.id} key={item?.id}>
                    {item?.label ?? ""}
                  </MenuItem>
                ))}
              </Select>
            </div>
            {refundType !== "none" && (
              <div className="flex items-center py-1.5">
                <CustomText className="text-13 min-w-64"></CustomText>
                <div>
                  <EditFormControl
                    className="w-64"
                    type="multiline"
                    name="refundReason"
                    rows={2}
                    fullWidth={true}
                    value={refundReason}
                    onChange={(e) => setRefundReason(e.target.value)}
                  />
                  <CustomText className="text-13 py-2">
                    A note is required when a provided reason isn't selected.
                  </CustomText>
                </div>
              </div>
            )}
          </div>
        </>
      </CustomDialog>
      <CustomDialog
        title="Moving Order to Future Week"
        maxWidth="sm"
        open={movingOpen}
        isAction={true}
        onClose={() => setMovingOpen(false)}
        // onSave={handleRefund}
      >
        <CustomText className="text-base">
          This action will re-schedule this order into a new Week Slot. The
          order cut-off date will be updated
        </CustomText>
        <div className="flex justify-end items-center p-5">
          <Button
            className="mx-2 px-4  font-bold font-zing text-base"
            color="primary"
            variant="contained"
            onClick={handleMoveConfirm}
          >
            Confirm
          </Button>
          <Button
            className="mx-2 px-4 font-bold font-zing text-base"
            color="secondary"
            variant="contained"
            onClick={() => setMovingOpen(false)}
          >
            Cancel
          </Button>
        </div>
      </CustomDialog>
      <iframe
        title="delivery ticket label"
        style={{ display: "none" }}
        id="printffull"
        src={fileUrl}
        name="printffull"
      ></iframe>
    </Paper>
  );
}
